import { AfterViewInit, Component, ElementRef, HostListener, Input, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { DTypeFormSignatureField } from '@grupo-constant/next-dtype/form';
import { DTypeViewableCol } from '@grupo-constant/next-dtype/types';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import SignaturePad from 'signature_pad';
import { DTypeFormFieldComponent } from '../../dtype-field.model';
import { FileService } from '../../../file/file.service';
import { HttpService } from '../../../http/http.service';
import { UserService } from '../../../user/user.service';
import { NextMethodsEnum } from '../../../enums/next-methods.enum';

@Component({
    selector: 'signature-field-component',
    templateUrl: './signature-field.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DtypeSignatureFieldComponent implements DTypeFormFieldComponent, OnInit, AfterViewInit {

    @ViewChild('canvas') canvas: any;
    @Input() field: DTypeFormSignatureField;
    @Input() isUpdate: boolean;
    @Input() useSummaryView: boolean;

    signaturePad: SignaturePad;
    image: string;
    editing = false;

    private unsubscribeAll = new Subject();

    constructor(
        private elementRef: ElementRef,
        private fileService: FileService,
        private http: HttpService,
        private userService: UserService,
    ) { }

    @HostListener('window:resize')
    onResize() {
        this.init();
    }

    ngOnInit() {
        const readonlyOnUpdate = (this.field.dTypeCol as DTypeViewableCol).readonlyOnUpdate;
        if (readonlyOnUpdate && this.isUpdate) {
            this.field.readonly = true;
        }

        this.field.control.valueChanges
            .pipe(takeUntil(this.unsubscribeAll))
            .subscribe((value) => {
                if (!value) {
                    this.image = undefined;
                }
            });

    }

    async ngAfterViewInit() {
        this.init();
        const darkTheme = document.body.classList.contains('dark');
        this.signaturePad = new SignaturePad(this.canvas.nativeElement, {
            penColor: darkTheme ? 'rgb(255,255,255)' : 'rgb(0,0,0)',
            backgroundColor: darkTheme ? 'rgb(0,0,0)' : 'rgb(255,255,255)',
            minWidth: 4,
            maxWidth: 4
        });
        if (this.field.control.getRawValue()) {
            this.image = await this.getImageInBase64(this.field.control.value);
        }
        if (this.image) {
            this.signaturePad.fromDataURL(this.image);
        }
    }

    init() {
        const ratio =  Math.max(window.devicePixelRatio || 1, 1);
        this.editing = true;
        setTimeout(() => {
            const canvas: any = this.elementRef.nativeElement.querySelector('canvas');// get current size of the canvas
            const rect = canvas.getBoundingClientRect();
            // canvas.width = window.innerWidth / 1.1;
            // canvas.height = window.innerHeight / 3;
            canvas.width = rect.width * ratio;
            canvas.height = (rect.height * ratio / 2);
            canvas.getContext('2d').scale(ratio, ratio);
            canvas.style.width = rect.width + 'px';
            canvas.style.height = (rect.height) / 2 + 'px';
            this.editing = false;
            if (this.signaturePad) {
                this.signaturePad.clear(); // Clear the pad on init
                if (this.image) {
                    this.signaturePad.fromDataURL(this.image);
                }
            }
        }, 500);
    }

    clear() {
        this.signaturePad.clear();
    }

    undo() {
        const data = this.signaturePad.toData();
        if (data) {
            data.pop(); // remove the last dot or line
            this.signaturePad.fromData(data);
        }
    }

    async toggleEditing() {
        this.editing = !this.editing;
        if (!this.editing) {
            this.image = this.signaturePad.toDataURL();
            await this.uploadFile();
        }
    }

    private async getImageInBase64(path: string) {
        const response = await firstValueFrom(this.http.get(`${NextMethodsEnum.downloadFile}&fileName=${path}&asJson=true`));
        return response?.json;
    }

    private async uploadFile() {
        try {
            const user = await firstValueFrom(this.userService.get());
            const path = await firstValueFrom(this.fileService.upload(user.userId + '_signature.png', this.image));
            this.field.control.setValue(path);
        } catch (error) {
            console.error(error);
        }
    }
}
